import GooglePlayBadge from 'assets/google-play-store-badge.svg';
import AppleStoreBadge from 'assets/apple-app-store-badge.svg';
import { useIntl } from '@leagueplatform/locales';
import { EVENT_NAME, trackAnalyticsEvent } from '@leagueplatform/analytics';
import { EXTERNAL_LINKS } from 'common/links';
import {
  AssetLinkProps,
  FooterLinkProps,
} from '@leagueplatform/solutions-foundation';
import { useExternalLinks } from 'hooks/use-external-links/use-external-links.hook';
import { usePatientPortalContext } from 'contexts/patient-portal-provider.context';
import { PATIENT_PORTAL_SYSTEM_IDS } from 'types/patient-portal-system.types';

export const useAppFooterLinks = () => {
  const { formatMessage } = useIntl();
  const { systemId } = usePatientPortalContext();
  const externalLinks = useExternalLinks();

  const isMyhealth = systemId === PATIENT_PORTAL_SYSTEM_IDS.MY_HEALTH;

  // League's Footer only navigates when its isExternalUrl() check passes, which
  // fails when a link's host matches the app host (as in prod, where the content
  // site shares the app's host). We open the URL manually in onClick so these
  // links always work.
  const footerLinks: FooterLinkProps[] = [
    ...(!isMyhealth && externalLinks.PRIVACY_COLLECTION_NOTICE
      ? [
          {
            text: formatMessage({ id: 'STR_COLLECTION_NOTICE' }),
            url: externalLinks.PRIVACY_COLLECTION_NOTICE,
            onClick: () => {
              trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
                product_area: 'foundation',
                sub_product_area: 'home screen',
                screen_name: 'home screen',
                detail: 'navigate to privacy collection notice',
                brand: [systemId],
              });
              window.open(
                externalLinks.PRIVACY_COLLECTION_NOTICE,
                '_blank',
                'noopener,noreferrer',
              );
            },
            linkProps: {
              indicator: undefined,
              target: '_blank',
              rel: 'noopener noreferrer',
            },
          },
        ]
      : []),
    ...(!isMyhealth && externalLinks.TERMS_OF_SERVICE
      ? [
          {
            text: formatMessage({ id: 'STR_TERMS_OF_SERVICE' }),
            url: externalLinks.TERMS_OF_SERVICE,
            onClick: () => {
              trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
                product_area: 'foundation',
                sub_product_area: 'home screen',
                screen_name: 'home screen',
                detail: 'navigate to terms of service',
                brand: [systemId],
              });
              window.open(
                externalLinks.TERMS_OF_SERVICE,
                '_blank',
                'noopener,noreferrer',
              );
            },
            linkProps: {
              indicator: undefined,
              target: '_blank',
              rel: 'noopener noreferrer',
            },
          },
        ]
      : []),
    ...(isMyhealth && externalLinks.PRIVACY_POLICY
      ? [
          {
            text: formatMessage({ id: 'STR_PRIVACY_POLICY' }),
            url: externalLinks.PRIVACY_POLICY,
            onClick: () => {
              trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
                product_area: 'foundation',
                sub_product_area: 'home screen',
                screen_name: 'home screen',
                detail: 'navigate to privacy policy',
                brand: [systemId],
              });
              window.open(
                externalLinks.PRIVACY_POLICY,
                '_blank',
                'noopener,noreferrer',
              );
            },
            linkProps: {
              indicator: undefined,
              target: '_blank',
              rel: 'noopener noreferrer',
            },
          },
        ]
      : []),
  ];

  const appDownloadLinks: AssetLinkProps[] = [
    {
      src: AppleStoreBadge,
      url: EXTERNAL_LINKS.APPLE_STORE_APP,
      alt: formatMessage({ id: 'STR_DOWNLOAD_APP_STORE' }),
      linkProps: {
        onClick: () => {
          trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
            product_area: 'foundation',
            sub_product_area: 'home screen',
            screen_name: 'home screen',
            detail: 'navigate to download on app store',
            brand: [systemId],
          });
        },
      },
    },
    {
      src: GooglePlayBadge,
      url: EXTERNAL_LINKS.GOOGLE_PLAY_STORE_APP,
      alt: formatMessage({ id: 'STR_DOWNLOAD_GOOGLE_PLAY' }),
      linkProps: {
        onClick: () => {
          trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
            product_area: 'foundation',
            sub_product_area: 'home screen',
            screen_name: 'home screen',
            detail: 'navigate to download on google play store',
            brand: [systemId],
          });
        },
      },
    },
  ];

  return {
    footerLinks,
    appDownloadLinks,
  };
};
